import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Calendar as CalendarIcon, FileText, Download } from "lucide-react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const STAGES = ["new", "to_contact", "contacted", "prequalification", "interview_scheduled", "interview_completed",
  "evaluation", "orientation", "training", "ready_deployment", "active", "progressing", "not_retained", "inactive"];

function Field({ label, value }) {
  return (
    <div className="border-b border-border py-2">
      <p className="text-xs uppercase tracking-widest text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm">{value === true ? "✓" : value === false ? "—" : value || "—"}</p>
    </div>
  );
}

export default function AdminCandidateDetail() {
  const { t } = useI18n();
  const { user } = useAuth();
  const { candidateId } = useParams();
  const [candidate, setCandidate] = useState(null);
  const [recruiters, setRecruiters] = useState([]);
  const [stage, setStage] = useState("");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [sensitive, setSensitive] = useState(false);
  const [task, setTask] = useState({ title: "", due_date: "" });
  const [documents, setDocuments] = useState([]);

  const canAssignOthers = ["super_admin", "admin", "manager"].includes(user?.role);
  const notFoundToastFor = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/candidates/${candidateId}`);
      setCandidate(data);
      setStage(data.pipeline_stage);
    } catch (error) {
      if (notFoundToastFor.current !== candidateId) {
        notFoundToastFor.current = candidateId;
        toast.error(error?.response?.status === 404
          ? t("admin.candidateNotFound")
          : apiError(error, t("common.error")));
      }
      setCandidate(false);
      return;
    }
    try {
      const docs = await api.get(`/candidates/${candidateId}/documents`);
      setDocuments(docs.data.items || []);
    } catch {
      setDocuments([]);
    }
  }, [candidateId, t]);

  const downloadDocument = async (doc) => {
    try {
      const response = await api.get(`/candidates/${candidateId}/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = doc.original_filename;
      anchor.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.status === 404 || error?.response?.status === 403
        ? t("admin.downloadDenied")
        : apiError(error, t("common.error")));
    }
  };

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (canAssignOthers) {
      api.get("/admin/recruiters").then(({ data }) => setRecruiters(data)).catch(() => setRecruiters([]));
    }
  }, [canAssignOthers]);

  const changeStage = async () => {
    try {
      await api.put(`/candidates/${candidateId}/stage`, { stage, reason: reason || null });
      setReason("");
      toast.success(t("admin.stageChanged"));
      load();
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    }
  };

  const assign = async (recruiterId) => {
    try {
      await api.put(`/candidates/${candidateId}/assign`, { recruiter_id: recruiterId || null });
      toast.success(t("admin.assignSuccess"));
      load();
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    }
  };

  const addNote = async (event) => {
    event.preventDefault();
    try {
      await api.post(`/candidates/${candidateId}/notes`, { body: note, is_sensitive: sensitive });
      setNote("");
      setSensitive(false);
      load();
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    }
  };

  const addTask = async (event) => {
    event.preventDefault();
    try {
      await api.post(`/candidates/${candidateId}/tasks`, { title: task.title, due_date: task.due_date || null });
      setTask({ title: "", due_date: "" });
      load();
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    }
  };

  const completeTask = async (taskId) => {
    try {
      await api.put(`/candidates/${candidateId}/tasks/${taskId}`, { status: "done" });
      load();
    } catch (error) {
      toast.error(apiError(error, t("common.error")));
    }
  };

  if (candidate === null) return <p className="text-sm text-muted-foreground">{t("common.loading")}</p>;
  if (candidate === false) return <p data-testid="candidate-not-found" className="text-sm text-destructive">{t("admin.candidateNotFound")}</p>;

  return (
    <div data-testid="admin-candidate-detail" className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="cs-kicker">{t("admin.profile")}</p>
          <h1 data-testid="candidate-name" className="mt-2 font-display text-3xl font-light sm:text-4xl">
            {candidate.first_name} {candidate.last_name}
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            {candidate.email} · {candidate.phone}
          </p>
        </div>
        <div className="border border-border bg-surface p-4">
          <p className="cs-kicker">{t("admin.table.stage")}</p>
          <p data-testid="candidate-stage" className="mt-1 font-sans text-xl font-medium text-accent">
            {t(`stages.${candidate.pipeline_stage}`)}
          </p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("admin.table.recruiter")}: <span data-testid="candidate-recruiter">{candidate.assigned_recruiter_name || t("admin.unassigned")}</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <section className="lg:col-span-4 space-y-6">
          <div className="border border-border bg-surface p-5">
            <h2 className="font-display text-xl">{t("admin.changeStage")}</h2>
            <select data-testid="stage-select" value={stage} onChange={(e) => setStage(e.target.value)}
                    className="mt-3 h-10 w-full border border-input bg-surface px-3 text-sm">
              {STAGES.map((key) => (
                <option key={key} value={key}>{t(`stages.${key}`)}</option>
              ))}
            </select>
            <Input data-testid="stage-reason" className="mt-3" placeholder={t("admin.stageReason")} value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button data-testid="stage-save" onClick={changeStage} className="mt-3 w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
              {t("common.save")}
            </Button>
          </div>

          {(canAssignOthers || candidate.assigned_recruiter_id !== user?.id) && (
          <div className="border border-border bg-surface p-5">
            <h2 className="font-display text-xl">{t("admin.assignTo")}</h2>
            {canAssignOthers ? (
              <select data-testid="assign-select" value={candidate.assigned_recruiter_id || ""} onChange={(e) => assign(e.target.value)}
                      className="mt-3 h-10 w-full border border-input bg-surface px-3 text-sm">
                <option value="">{t("admin.unassigned")}</option>
                {recruiters.map((recruiter) => (
                  <option key={recruiter.id} value={recruiter.id}>
                    {recruiter.first_name} {recruiter.last_name}
                  </option>
                ))}
              </select>
            ) : (
              <Button data-testid="claim-button" onClick={() => assign(user.id)} variant="outline" className="mt-3 w-full rounded-full">
                {t("admin.claim")}
              </Button>
            )}
          </div>
          )}

          <div className="border border-border bg-surface p-5">
            <h2 className="font-display text-xl">{t("admin.history")}</h2>
            <ol data-testid="stage-history" className="mt-3 space-y-2 text-sm">
              {(candidate.stage_history || []).map((entry) => (
                <li key={entry.id} className="border-b border-border pb-2">
                  <span className="text-muted-foreground">{entry.from_stage ? t(`stages.${entry.from_stage}`) : "—"} → </span>
                  <span>{t(`stages.${entry.to_stage}`)}</span>
                  <div className="text-xs text-muted-foreground">
                    {(entry.created_at || "").slice(0, 16).replace("T", " ")} · {entry.actor_email || "system"}
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="lg:col-span-4 space-y-6">
          <div className="border border-border bg-surface p-5">
            <h2 className="font-display text-xl">{t("admin.notes")}</h2>
            <form onSubmit={addNote} className="mt-3 space-y-3" data-testid="note-form">
              <Textarea data-testid="note-input" rows={3} required placeholder={t("admin.notePlaceholder")} value={note} onChange={(e) => setNote(e.target.value)} />
              <label className="flex items-center gap-2 text-xs">
                <Checkbox data-testid="note-sensitive" checked={sensitive} onCheckedChange={(v) => setSensitive(Boolean(v))} />
                {t("admin.sensitive")}
              </label>
              <Button type="submit" data-testid="note-submit" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                {t("admin.addNote")}
              </Button>
            </form>
            <ul data-testid="notes-list" className="mt-4 space-y-3">
              {(candidate.notes || []).length === 0 && <li className="text-sm text-muted-foreground">{t("admin.noNotes")}</li>}
              {(candidate.notes || []).map((item) => (
                <li key={item.id} className="border-b border-border pb-2 text-sm">
                  <p>{item.body}</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {item.author_email} · {(item.created_at || "").slice(0, 16).replace("T", " ")}
                    {item.is_sensitive && <span className="ml-2 text-accent">{t("admin.sensitive")}</span>}
                  </p>
                </li>
              ))}
            </ul>
          </div>

          <div className="border border-border bg-surface p-5">
            <h2 className="font-display text-xl">{t("admin.tasks")}</h2>
            <form onSubmit={addTask} className="mt-3 space-y-3" data-testid="task-form">
              <div>
                <Label htmlFor="task-title">{t("admin.taskTitle")}</Label>
                <Input id="task-title" data-testid="task-title" required value={task.title} onChange={(e) => setTask({ ...task, title: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label htmlFor="task-due">{t("admin.dueDate")}</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" data-testid="task-due-trigger" className="mt-1 w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {task.due_date || t("admin.dueDate")}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={task.due_date ? new Date(`${task.due_date}T00:00:00`) : undefined}
                      onSelect={(date) =>
                        setTask({ ...task, due_date: date ? date.toISOString().slice(0, 10) : "" })
                      }
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
                <input type="hidden" data-testid="task-due" value={task.due_date} readOnly />
              </div>
              <Button type="submit" data-testid="task-submit" className="w-full rounded-full bg-accent text-accent-foreground hover:bg-accent/90">
                {t("admin.addTask")}
              </Button>
            </form>
            <ul data-testid="tasks-list" className="mt-4 space-y-3">
              {(candidate.tasks || []).length === 0 && <li className="text-sm text-muted-foreground">{t("admin.noTasksCandidate")}</li>}
              {(candidate.tasks || []).map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-3 border-b border-border pb-2 text-sm">
                  <div>
                    <p>{item.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {item.due_date || "—"} · {item.status === "done" ? t("admin.done") : t("admin.open")}
                    </p>
                  </div>
                  {item.status !== "done" && (
                    <button data-testid={`task-complete-${item.id}`} onClick={() => completeTask(item.id)} className="text-xs text-accent hover:underline">
                      {t("admin.markDone")}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="lg:col-span-4">
          <div className="border border-border bg-surface p-5">
            <h2 className="font-display text-xl">{t("documents.staffTitle")}</h2>
            {documents.length === 0 ? (
              <p data-testid="staff-documents-empty" className="mt-3 text-sm text-muted-foreground">
                {t("documents.empty")}
              </p>
            ) : (
              <ul data-testid="staff-documents-list" className="mt-3 divide-y divide-border">
                {documents.map((doc) => (
                  <li key={doc.id} data-testid={`staff-document-${doc.id}`} className="flex items-center gap-3 py-3 text-sm">
                    <FileText className="h-4 w-4 shrink-0 text-accent" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate">{doc.original_filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {t(`documents.types.${doc.document_type}`)} · {(doc.size_bytes / 1024).toFixed(0)} KB ·{" "}
                        {(doc.uploaded_at || "").slice(0, 10)}
                      </p>
                    </div>
                    {(canAssignOthers || candidate.assigned_recruiter_id === user?.id) && (
                      <button
                        type="button"
                        data-testid={`staff-document-download-${doc.id}`}
                        onClick={() => downloadDocument(doc)}
                        className="shrink-0 text-xs text-accent hover:underline"
                      >
                        <Download className="mr-1 inline h-3.5 w-3.5" />
                        {t("documents.download")}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-6 border border-border bg-surface p-5">
            <h2 className="font-display text-xl">{t("admin.profile")}</h2>
            <div className="mt-3">
              <Field label={t("apply.fields.city")} value={`${candidate.city || ""} ${candidate.province_state || ""}`} />
              <Field label={t("apply.fields.country")} value={candidate.country} />
              <Field label={t("apply.fields.employmentStatus")} value={candidate.employment_status} />
              <Field label={t("apply.fields.currentRole")} value={candidate.current_role} />
              <Field label={t("apply.fields.yearsExperience")} value={candidate.years_experience} />
              <Field label={t("apply.fields.education")} value={candidate.education} />
              <Field label={t("apply.fields.availability")} value={candidate.availability} />
              <Field label={t("apply.fields.interests")} value={(candidate.interests || []).map((v) => t(`verticals.${v}`)).join(", ")} />
              <Field label={t("apply.fields.careerGoals")} value={candidate.career_goals} />
              <Field label={t("apply.fields.motivation")} value={candidate.motivation} />
            </div>
            <h3 className="mt-6 font-display text-lg">{t("admin.consent")}</h3>
            <div className="mt-2">
              <Field label={t("apply.consent.age")} value={candidate.age_confirmation_18_plus} />
              <Field label={t("apply.consent.marketing")} value={candidate.marketing_consent} />
              <Field label={t("apply.consent.version")} value={candidate.consent_version} />
              <Field label="consent_timestamp" value={(candidate.consent_timestamp || "").slice(0, 16).replace("T", " ")} />
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
